
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  source: 'error',
  description: 'Guide to understanding and troubleshooting errors specific to Signal endpoints',
  title: 'Signal'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const ErrorReference = makeShortcode("ErrorReference");
const Checkbox = makeShortcode("Checkbox");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Signal errors' subtitle='Guide to troubleshooting Signal errors' mdxType="Header" />
    <h4 {...{
      "id": "client_transaction_id_already_in_use"
    }}><strong parentName="h4">{`CLIENT_TRANSACTION_ID_ALREADY_IN_USE`}</strong></h4>
    <ErrorReference type='SIGNAL_ERROR' error='CLIENT_TRANSACTION_ID_ALREADY_IN_USE' subtitle="The `client_transaction_id` value specified is not unique" server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The `}<inlineCode parentName="li">{`client_transaction_id`}</inlineCode>{` was used by a transaction whose data has since been deleted (for example, via a data-deletion request). Otherwise, reusing an existing `}<inlineCode parentName="li">{`client_transaction_id`}</inlineCode>{` returns the original evaluation response rather than this error.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Ensure that you use a unique `}<inlineCode parentName="p">{`client_transaction_id`}</inlineCode>{` for each evaluation, even if the transaction has the same transaction details (e.g. a transaction for the same amount, by the same end user) as a previous transaction attempt.  `}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "invalid_configuration_state"
    }}><strong parentName="h4">{`INVALID_CONFIGURATION_STATE`}</strong></h4>
    <ErrorReference type='SIGNAL_ERROR' error='INVALID_CONFIGURATION_STATE' subtitle="/signal/evaluate was called by a client whose Signal Rules configuration settings are in an invalid state" server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-1"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`A customer who is not enabled for Signal Transaction Scores called `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{` with no ruleset key specified, while opted out of the default ruleset.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-1"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Only customers who are enabled for Signal Transaction Scores are allowed to opt out of the default ruleset, so this error should never occur and indicates that your account configuration is in an invalid state. Contact your account manager or file a support ticket to repair your account state.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`As an interim workaround, either specify a `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, or use `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` instead.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "not_enabled_for_signal_transaction_score_rulesets"
    }}><strong parentName="h4">{`NOT_ENABLED_FOR_SIGNAL_TRANSACTION_SCORE_RULESETS`}</strong></h4>
    <ErrorReference type='SIGNAL_ERROR' error='NOT_ENABLED_FOR_SIGNAL_TRANSACTION_SCORE_RULESETS' subtitle="A ruleset was specified that uses Signal Transaction Scores, but the customer is only enabled for Balance-only rulesets" server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-2"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`A customer who has previously used Signal Transaction Scores, but has since downgraded to Balance-only, attempted to use a ruleset that requires Signal Transaction Scores when calling `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{`.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-2"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`To use Signal Transaction Scores, contact your account manager and request that Signal Transaction Scores be enabled for your account, or submit a `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/settings/team/products"
          }}>{`product add request in the Dashboard`}</a>{` for the Signal Transaction Scores product.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If you do not want to use Signal Transaction Scores, convert the ruleset to Balance-only or use a different ruleset key.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "ruleset_not_found"
    }}><strong parentName="h4">{`RULESET_NOT_FOUND`}</strong></h4>
    <ErrorReference type='SIGNAL_ERROR' error='RULESET_NOT_FOUND' subtitle="The specified ruleset was not found" client server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-3"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`When calling `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{`, no `}<inlineCode parentName="li">{`ruleset_key`}</inlineCode>{` was specified, and the default ruleset has not been created.`}</li>
        <li parentName="ul">{`When calling `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{`, a `}<inlineCode parentName="li">{`ruleset_key`}</inlineCode>{` was specified, and no matching ruleset could be found.`}</li>
        <li parentName="ul">{`When switching between Sandbox and Production, a `}<inlineCode parentName="li">{`ruleset_key`}</inlineCode>{` was specified that exists only in Sandbox or only in Production.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-3"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`If no `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{` was specified, create a default Ruleset via the Dashboard under `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/signal/risk-profiles"
          }}><strong parentName="a">{`Signal > Rules`}</strong></a>{`.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If a `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{` was specified, check the Dashboard under `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/signal/risk-profiles"
          }}><strong parentName="a">{`Signal > Rules`}</strong></a>{` to make sure that there is a ruleset matching the `}<inlineCode parentName="p">{`ruleset_key`}</inlineCode>{`. `}</p>
        <ul>
          <li parentName="ul">
            <p parentName="li">{`Make sure you are checking the correct environment's ruleset list (i.e. if you are calling `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` in Production, set the slider in the Dashboard to show Production rulesets). `}</p>
          </li>
          <li parentName="ul">
            <p parentName="li">{`Make sure the `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` of the Plaid Team in the Dashboard, which can be found under `}<a parentName="p" {...{
                "href": "https://dashboard.plaid.com/developers/keys"
              }}><strong parentName="a">{`Build > Keys`}</strong></a>{`, matches the `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` used to make the API call.`}</p>
          </li>
        </ul>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If you do not want to use a ruleset and are a Signal Transaction Scores customer, contact your account manager to opt out of default Signal Rulesets. `}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If you do not want to use a ruleset and are not a Signal Transaction Scores customer, use `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` to get balance data. `}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "signal_transaction_not_initiated"
    }}><strong parentName="h4">{`SIGNAL_TRANSACTION_NOT_INITIATED`}</strong></h4>
    <ErrorReference type='SIGNAL_ERROR' error='SIGNAL_TRANSACTION_NOT_INITIATED' subtitle="A return was reported on a transaction that was also reported never to have happened." client server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-4"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`A return was reported on the transaction using `}<inlineCode parentName="li">{`/signal/return/report`}</inlineCode>{`, but `}<inlineCode parentName="li">{`/signal/decision/report`}</inlineCode>{` was already called for the same transaction with a value of `}<inlineCode parentName="li">{`initiated: false`}</inlineCode>{`.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-4"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`If you change your verdict on whether to process a transaction after calling `}<inlineCode parentName="p">{`/signal/decision/report`}</inlineCode>{`, or if you make a decision that differs from the Signal Ruleset evaluation (processing a transaction that evaluated to `}<inlineCode parentName="p">{`REROUTE`}</inlineCode>{`, or not processing a transaction that evaluated to `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{`), correct Plaid's records by calling `}<inlineCode parentName="p">{`/signal/decision/report`}</inlineCode>{` with the most up-to-date verdict about your decision. `}</p>
      </Checkbox>
    </ErrorReference>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "client_transaction_id_already_in_use",
        level: 4,
        title: "CLIENT_TRANSACTION_ID_ALREADY_IN_USE",
      },
{
        id: "invalid_configuration_state",
        level: 4,
        title: "INVALID_CONFIGURATION_STATE",
      },
{
        id: "not_enabled_for_signal_transaction_score_rulesets",
        level: 4,
        title: "NOT_ENABLED_FOR_SIGNAL_TRANSACTION_SCORE_RULESETS",
      },
{
        id: "ruleset_not_found",
        level: 4,
        title: "RULESET_NOT_FOUND",
      },
{
        id: "signal_transaction_not_initiated",
        level: 4,
        title: "SIGNAL_TRANSACTION_NOT_INITIATED",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
